/**
 * Hover-intent driven controller for the main mega-menu.
 *
 * Behaviour:
 *  - Top-level (root) items with children open after a short hover-intent
 *    delay. If another root is already open, the new one opens instantly so
 *    the menu feels snappy when the user is moving along the bar.
 *  - When the cursor leaves a root item, the dropdown stays open for a short
 *    grace period before closing, so the user can move into the dropdown
 *    panel even diagonally.
 *  - While a root's dropdown is still finishing its CSS close transition,
 *    re-entering that same root is ignored. Otherwise the still-visible
 *    fading panel would snap back to fully open and look like a flicker.
 *  - Sub-items with children open the next column after a small intent
 *    delay. Switching between siblings ALSO uses a short delay so the user
 *    can move the cursor diagonally back toward an already-open deeper
 *    column without other rows on the way stealing focus.
 *  - Sub-items _without_ children also clear any active sibling, but only
 *    after a short intent delay. This way a quick diagonal sweep across leaf
 *    rows on the way to the deeper column does NOT collapse it, while
 *    actually resting on a leaf collapses it so the highlight only ever
 *    appears on a single row at a time.
 *  - All visibility is driven by the `--active` class. The CSS no longer
 *    relies on `:hover` so we don't lose state when the cursor crosses the
 *    scrollbar gutter or any small layout gap.
 */

const ROOT_OPEN_DELAY = 150;
const ROOT_CLOSE_DELAY = 200;
// Matches the .mega__dropdown opacity/visibility transition duration in
// _navigation.scss. Used to debounce re-opens of a root whose close
// animation hasn't finished yet.
const ROOT_CLOSE_ANIMATION_MS = 200;
const SUB_OPEN_DELAY = 60;
const SUB_SWITCH_DELAY = 120;
const SUB_LEAF_CLEAR_DELAY = 150;

const ROOT_ITEM_SELECTOR = '.main__navigation__item.--has-children';
const SUB_ITEM_SELECTOR = '.mega__item';
const SUB_PARENT_ITEM_SELECTOR = '.mega__item.--has-children';
const ACTIVE_CLASS = '--active';

class MegaNavigationController {
  private readonly root: HTMLElement;
  private activeRoot: HTMLElement | null = null;
  private rootOpenTimer: number | null = null;
  private rootCloseTimer: number | null = null;
  private rootClosingItem: HTMLElement | null = null;
  private rootClosingTimer: number | null = null;

  public constructor(root: HTMLElement) {
    this.root = root;
  }

  public init(): void {
    this.root
      .querySelectorAll<HTMLElement>(ROOT_ITEM_SELECTOR)
      .forEach((item) => this.bindRootItem(item));

    this.root
      .querySelectorAll<HTMLElement>(SUB_ITEM_SELECTOR)
      .forEach((item) => this.bindSubItem(item));
  }

  private hasChildren(item: HTMLElement): boolean {
    return item.matches(SUB_PARENT_ITEM_SELECTOR);
  }

  private bindRootItem(item: HTMLElement): void {
    item.addEventListener('mouseenter', () => {
      this.cancelRootClose();

      if (this.activeRoot === item) {
        return;
      }

      // If another root is already open, switch instantly: the user is
      // already exploring the menu bar.
      if (this.activeRoot) {
        this.openRoot(item);
        return;
      }

      // This root is still finishing its CSS close transition. Re-opening
      // now would snap the fading-out dropdown back to fully open and read
      // as a flicker; let it finish closing instead.
      if (this.rootClosingItem === item) {
        return;
      }

      this.cancelRootOpen();
      this.rootOpenTimer = window.setTimeout(() => {
        this.openRoot(item);
      }, ROOT_OPEN_DELAY);
    });

    item.addEventListener('mouseleave', () => {
      this.cancelRootOpen();
      this.cancelRootClose();
      this.rootCloseTimer = window.setTimeout(() => {
        if (this.activeRoot === item) {
          this.closeRoot();
        }
      }, ROOT_CLOSE_DELAY);
    });
  }

  private bindSubItem(item: HTMLElement): void {
    let pendingTimer: number | null = null;

    const cancelPending = () => {
      if (pendingTimer != null) {
        window.clearTimeout(pendingTimer);
        pendingTimer = null;
      }
    };

    item.addEventListener('mouseenter', () => {
      cancelPending();

      const parentColumn = item.parentElement;
      if (!parentColumn) {
        return;
      }

      const itemHasChildren = this.hasChildren(item);
      const hasActiveSibling = !!parentColumn.querySelector(
        `:scope > ${SUB_PARENT_ITEM_SELECTOR}.${ACTIVE_CLASS}`,
      );

      // Items with children: activate this item (which opens the next
      // column). Items without children: just clear any active sibling so
      // the deeper column closes and only one row is highlighted at a time.
      const apply = () => {
        this.deactivateSiblings(parentColumn, item);
        if (itemHasChildren) {
          item.classList.add(ACTIVE_CLASS);
        }
        if (this.activeRoot) {
          this.applyActiveChainSize(this.activeRoot);
        }
      };

      if (itemHasChildren) {
        if (item.classList.contains(ACTIVE_CLASS)) {
          return;
        }
        // Use a slightly longer delay when there's already an active
        // sibling: the user might be sweeping the cursor diagonally back
        // toward the open deeper column and we don't want intermediate
        // rows to steal focus.
        const delay = hasActiveSibling ? SUB_SWITCH_DELAY : SUB_OPEN_DELAY;
        pendingTimer = window.setTimeout(apply, delay);
        return;
      }

      // Leaf item — only act if there's actually something to clear.
      if (!hasActiveSibling) {
        return;
      }
      pendingTimer = window.setTimeout(apply, SUB_LEAF_CLEAR_DELAY);
    });

    item.addEventListener('mouseleave', () => {
      // Cancel any pending state change. Whatever is currently active stays
      // until the next intentional hover claims it or the root closes.
      cancelPending();
    });
  }

  private openRoot(item: HTMLElement): void {
    if (this.activeRoot === item) {
      return;
    }

    if (this.activeRoot) {
      this.deactivateRoot(this.activeRoot);
    }

    this.activeRoot = item;
    item.classList.add(ACTIVE_CLASS);
    this.applyActiveChainSize(item);
  }

  private closeRoot(): void {
    if (!this.activeRoot) {
      return;
    }
    const closing = this.activeRoot;
    closing
      .querySelector<HTMLElement>('.mega__dropdown__inner')
      ?.style.removeProperty('min-height');
    this.deactivateRoot(closing);
    this.activeRoot = null;

    this.rootClosingItem = closing;
    if (this.rootClosingTimer != null) {
      window.clearTimeout(this.rootClosingTimer);
    }
    this.rootClosingTimer = window.setTimeout(() => {
      this.rootClosingItem = null;
      this.rootClosingTimer = null;
    }, ROOT_CLOSE_ANIMATION_MS);
  }

  /**
   * Set the inner's `min-height` to the tallest natural content height in
   * the currently active chain (L1 + active L2 + active L3).
   *
   * We deliberately sum the rows' bounding rects instead of reading
   * `scrollHeight`. A row's height doesn't change when the column itself
   * is stretched, so the measurement is stable across re-measures — no
   * feedback loop where each hover grows the panel a few pixels.
   */
  private applyActiveChainSize(rootItem: HTMLElement): void {
    const dropdown = rootItem.querySelector<HTMLElement>('.mega__dropdown');
    const inner = rootItem.querySelector<HTMLElement>(
      '.mega__dropdown__inner',
    );
    if (!dropdown || !inner) {
      return;
    }

    const chain: Array<HTMLElement | null> = [
      dropdown.querySelector<HTMLElement>('.mega__column--level-1'),
      dropdown.querySelector<HTMLElement>(
        '.mega__column--level-1 > .mega__column__list > .mega__item.--active > .mega__column--level-2',
      ),
      dropdown.querySelector<HTMLElement>(
        '.mega__column--level-1 > .mega__column__list > .mega__item.--active > .mega__column--level-2 > .mega__column__list > .mega__item.--active > .mega__column--level-3',
      ),
    ];

    let max = 0;
    chain.forEach((col) => {
      if (!col) {
        return;
      }
      const h = this.columnContentHeight(col);
      if (h > max) {
        max = h;
      }
    });

    if (max > 0) {
      inner.style.minHeight = `${max}px`;
    } else {
      inner.style.removeProperty('min-height');
    }
  }

  private columnContentHeight(column: HTMLElement): number {
    let h = 0;
    const header = column.querySelector<HTMLElement>(
      ':scope > .mega__column__header',
    );
    if (header) {
      h += header.getBoundingClientRect().height;
    }
    const list = column.querySelector<HTMLElement>(
      ':scope > .mega__column__list',
    );
    if (list) {
      const styles = getComputedStyle(list);
      h += parseFloat(styles.paddingTop) || 0;
      h += parseFloat(styles.paddingBottom) || 0;
      Array.from(list.children).forEach((child) => {
        h += (child as HTMLElement).getBoundingClientRect().height;
      });
    }
    return Math.ceil(h);
  }

  private deactivateRoot(item: HTMLElement): void {
    item.classList.remove(ACTIVE_CLASS);
    item
      .querySelectorAll<HTMLElement>(`.mega__item.${ACTIVE_CLASS}`)
      .forEach((el) => el.classList.remove(ACTIVE_CLASS));
  }

  private deactivateSiblings(
    parent: HTMLElement,
    keep: HTMLElement,
  ): void {
    parent
      .querySelectorAll<HTMLElement>(`:scope > .mega__item.${ACTIVE_CLASS}`)
      .forEach((sibling) => {
        if (sibling === keep) {
          return;
        }
        sibling.classList.remove(ACTIVE_CLASS);
        sibling
          .querySelectorAll<HTMLElement>(`.mega__item.${ACTIVE_CLASS}`)
          .forEach((descendant) => descendant.classList.remove(ACTIVE_CLASS));
      });
  }

  private cancelRootOpen(): void {
    if (this.rootOpenTimer != null) {
      window.clearTimeout(this.rootOpenTimer);
      this.rootOpenTimer = null;
    }
  }

  private cancelRootClose(): void {
    if (this.rootCloseTimer != null) {
      window.clearTimeout(this.rootCloseTimer);
      this.rootCloseTimer = null;
    }
  }
}

export function initMegaNavigation(): void {
  document
    .querySelectorAll<HTMLElement>('.main__navigation')
    .forEach((nav) => new MegaNavigationController(nav).init());
}
